import React from 'react';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  Clock,
  Users,
  GitMerge,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useDuplicateReport } from '@/hooks/useDuplicates';
import { cn } from '@/lib/utils';

interface DuplicateReportPanelProps {
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
}

export const DuplicateReportPanel: React.FC<DuplicateReportPanelProps> = ({ dateRange }) => {
  const { data: report, isLoading, error } = useDuplicateReport(dateRange);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-3/4"></div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (error || !report) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <p className="text-red-600">Failed to load duplicate report</p>
        </div>
      </Card>
    );
  }

  const statusColors = {
    'PENDING': 'bg-yellow-100 text-yellow-800',
    'REVIEWING': 'bg-blue-100 text-blue-800',
    'MERGED': 'bg-green-100 text-green-800',
    'DISMISSED': 'bg-gray-100 text-gray-800',
    'FLAGGED': 'bg-red-100 text-red-800'
  };

  const matchTypeColors = {
    'EXACT_EMAIL': 'bg-red-500',
    'EXACT_PHONE': 'bg-orange-500',
    'FUZZY_NAME_COMPANY': 'bg-yellow-500',
    'MULTIPLE_FIELDS': 'bg-purple-500'
  };

  // The backend may return byMatchType/byStatus either as arrays
  // ([{ matchType/status, count }]) or as plain objects ({ EXACT_EMAIL: 5, ... }).
  // Normalize both shapes to arrays so .map()/.reduce() never crash.
  const reportAny = report as any;
  const byMatchTypeArray: { matchType: string; count: number }[] = Array.isArray(reportAny.byMatchType)
    ? reportAny.byMatchType
    : Object.entries(reportAny.byMatchType || {}).map(([matchType, count]) => ({
        matchType,
        count: Number(count ?? 0),
      }));
  const byStatusArray: { status: string; count: number }[] = Array.isArray(reportAny.byStatus)
    ? reportAny.byStatus
    : Object.entries(reportAny.byStatus || {}).map(([status, count]) => ({
        status,
        count: Number(count ?? 0),
      }));

  // mergeRate may be a fraction (0–1) or already a percentage; treat as fraction.
  const mergeRatePct = Math.round(Number(reportAny.mergeRate ?? 0) * 100);

  // Avg resolution time can arrive as a number (avgResolutionTimeHours) or as a
  // string like '24 hours' (avgTimeToResolve). parseFloat handles both safely.
  const avgResolutionHours = Number.isFinite(parseFloat(reportAny.avgResolutionTimeHours))
    ? parseFloat(reportAny.avgResolutionTimeHours)
    : parseFloat(reportAny.avgTimeToResolve) || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Sets */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Sets</p>
              <p className="text-2xl font-bold text-gray-900">{report.summary.totalSets}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <GitMerge className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </Card>

        {/* Pending Review */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Review</p>
              <p className="text-2xl font-bold text-yellow-600">{report.summary.pendingSets}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </Card>

        {/* Merge Rate */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Merge Rate</p>
              <p className="text-2xl font-bold text-green-600">
                {mergeRatePct}%
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </Card>

        {/* Avg Resolution Time */}
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Resolution</p>
              <p className="text-2xl font-bold text-purple-600">
                {avgResolutionHours.toFixed(1)}h
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Match Type */}
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <PieChart className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">By Match Type</h3>
          </div>

          <div className="space-y-3">
            {byMatchTypeArray.map(item => {
              const total = byMatchTypeArray.reduce((sum, i) => sum + i.count, 0);
              const percentage = total > 0 ? (item.count / total) * 100 : 0;

              return (
                <div key={item.matchType}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-gray-600">
                      {item.matchType.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm font-medium text-gray-900">
                      {item.count}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={cn(
                        "h-2 rounded-full",
                        matchTypeColors[item.matchType as keyof typeof matchTypeColors] || 'bg-gray-500'
                      )}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}

            {byMatchTypeArray.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </Card>

        {/* By Status */}
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">By Status</h3>
          </div>

          <div className="space-y-3">
            {byStatusArray.map(item => (
              <div key={item.status} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  {item.status === 'PENDING' && <Clock className="h-4 w-4 text-yellow-600" />}
                  {item.status === 'REVIEWING' && <AlertTriangle className="h-4 w-4 text-blue-600" />}
                  {item.status === 'MERGED' && <CheckCircle className="h-4 w-4 text-green-600" />}
                  {item.status === 'DISMISSED' && <XCircle className="h-4 w-4 text-gray-600" />}
                  {item.status === 'FLAGGED' && <AlertTriangle className="h-4 w-4 text-red-600" />}
                  <span className="text-sm text-gray-600">{item.status}</span>
                </div>
                <span className={cn(
                  "px-2 py-1 text-xs font-medium rounded-full",
                  statusColors[item.status as keyof typeof statusColors]
                )}>
                  {item.count}
                </span>
              </div>
            ))}

            {byStatusArray.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">No data available</p>
            )}
          </div>
        </Card>
      </div>

      {/* Top Duplicate Creators */}
      {report.topCreators && report.topCreators.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Top Duplicate Creators</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-xs font-semibold text-gray-600">User</th>
                  <th className="text-right py-2 text-xs font-semibold text-gray-600">Duplicates</th>
                </tr>
              </thead>
              <tbody>
                {report.topCreators.map((creator, index) => (
                  <tr key={creator.userId} className="border-b border-gray-100">
                    <td className="py-3">
                      <div className="flex items-center space-x-2">
                        <div className={cn(
                          "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white",
                          index === 0 ? "bg-yellow-500" :
                          index === 1 ? "bg-gray-400" :
                          index === 2 ? "bg-orange-400" : "bg-blue-500"
                        )}>
                          {index + 1}
                        </div>
                        <span className="text-sm text-gray-900">{creator.userName}</span>
                      </div>
                    </td>
                    <td className="py-3 text-right">
                      <span className="text-sm font-medium text-gray-900">
                        {creator.duplicateCount}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Daily Trends */}
      {report.trendsDaily && report.trendsDaily.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Calendar className="h-5 w-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Daily Trends</h3>
          </div>

          <div className="overflow-x-auto">
            <div className="min-w-[400px]">
              {/* Simple bar chart representation */}
              <div className="flex items-end space-x-2 h-32">
                {report.trendsDaily.slice(-14).map((day, index) => {
                  const maxValue = Math.max(
                    ...report.trendsDaily.map(d => Math.max(d.detected, d.resolved))
                  );
                  const detectedHeight = maxValue > 0 ? (day.detected / maxValue) * 100 : 0;
                  const resolvedHeight = maxValue > 0 ? (day.resolved / maxValue) * 100 : 0;

                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center">
                      <div className="flex-1 flex items-end space-x-0.5 w-full">
                        <div
                          className="flex-1 bg-red-400 rounded-t"
                          style={{ height: `${detectedHeight}%` }}
                          title={`Detected: ${day.detected}`}
                        />
                        <div
                          className="flex-1 bg-green-400 rounded-t"
                          style={{ height: `${resolvedHeight}%` }}
                          title={`Resolved: ${day.resolved}`}
                        />
                      </div>
                      <span className="text-xs text-gray-500 mt-1 transform -rotate-45 origin-top-left">
                        {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center space-x-4 mt-8">
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-red-400 rounded" />
                  <span className="text-xs text-gray-600">Detected</span>
                </div>
                <div className="flex items-center space-x-1">
                  <div className="w-3 h-3 bg-green-400 rounded" />
                  <span className="text-xs text-gray-600">Resolved</span>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};
